/**
 * Error codes the Word import can fail with. The message is shown to the
 * author, so it says what to do rather than what went wrong internally.
 */
export const DOCX_ERRORS = Object.freeze({
    NOT_A_ZIP: 'This file is not a Word document (.docx).',
    LEGACY_DOC: 'This looks like an old .doc file. Open it in Word and save it as .docx first.',
    ENCRYPTED: 'This document is password-protected. Remove the password in Word and try again.',
    NOT_WORDML: 'This file is not a Word document (.docx).',
    LIMIT_INPUT: 'This document is too large to import.',
    LIMIT_ENTRIES: 'This document contains too many parts to import.',
    LIMIT_PART: 'This document is too large to import.',
    LIMIT_RATIO: 'This document is malformed and cannot be imported.',
    LIMIT_XML: 'This document is too complex to import.',
    ZIP64: 'This document is too large to import.',
    XML_MALFORMED: 'This document is damaged and cannot be imported.',
    DOCTYPE_FORBIDDEN: 'This document contains forbidden content and cannot be imported.',
    TIMEOUT: 'Importing took too long. Try splitting the document into smaller parts.',
    WORKER_UNAVAILABLE: 'Word import is not available in this browser.',
    CONTENT_TOO_LARGE: 'The imported document is too large for this text field.',
    TARGET_READONLY: 'The editor became read-only, so nothing was imported.',
    ABORTED: 'The import was cancelled.',
    INTERNAL: 'Something went wrong while importing this document.',
});

export class DocxImportError extends Error {
    /**
     * @param {keyof typeof DOCX_ERRORS} code
     * @param {string} [detail] extra context for logs; never shown to the author
     */
    constructor(code, detail) {
        super(DOCX_ERRORS[code] || DOCX_ERRORS.INTERNAL);
        this.name = 'DocxImportError';
        this.code = DOCX_ERRORS[code] ? code : 'INTERNAL';
        this.detail = detail;
    }
}

/** Normalises anything thrown during an import into a DocxImportError. */
export function toDocxError(err) {
    if (err instanceof DocxImportError) return err;
    if (err && typeof err.code === 'string' && DOCX_ERRORS[err.code]) {
        return new DocxImportError(err.code, err.detail || err.message);
    }
    return new DocxImportError('INTERNAL', err && err.message ? err.message : String(err));
}
